import { formatMessage } from "i18n/CurrentLocale";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { ReduxState } from "types";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { updateGtmConsent } from "myParkdUtils/googleAnalytics";


// TODO: keep only one source of truth (see 'messages')
const SUPPORTED_LANGUAGES = ['en', 'nl', 'fr', 'de'];

// Called by vanilla-cookieconsent when it switches to a language, so formatMessage() returns strings for the active locale.
const buildTranslation = (): CookieConsent.Translation => ({
    consentModal: {
        title: formatMessage({ id: 'COOKIE_CONSENT_CONSENT_MODAL_TITLE' }),
        description: formatMessage({ id: 'COOKIE_CONSENT_CONSENT_MODAL_DESCRIPTION' }),
        acceptAllBtn: formatMessage({ id: 'COOKIE_CONSENT_BTN_ACCEPT_ALL' }),
        acceptNecessaryBtn: formatMessage({ id: 'COOKIE_CONSENT_BTN_ACCEPT_NECESSARY' }),
        showPreferencesBtn: formatMessage({ id: 'COOKIE_CONSENT_BTN_SHOW_PREFERENCES' }),
    },
    preferencesModal: {
        title: formatMessage({ id: 'COOKIE_CONSENT_PREFERENCES_MODAL_TITLE' }),
        acceptAllBtn: formatMessage({ id: 'COOKIE_CONSENT_BTN_ACCEPT_ALL' }),
        acceptNecessaryBtn: formatMessage({ id: 'COOKIE_CONSENT_BTN_ACCEPT_NECESSARY' }),
        savePreferencesBtn: formatMessage({ id: 'COOKIE_CONSENT_BTN_SAVE_PREFERENCES' }),
        closeIconLabel: formatMessage({ id: 'COOKIE_CONSENT_BTN_CLOSE' }),
        sections: [
            {
                title: formatMessage({ id: 'COOKIE_CONSENT_SECTION_NECESSARY_TITLE' }),
                description: formatMessage({ id: 'COOKIE_CONSENT_SECTION_NECESSARY_DESCRIPTION' }),
                linkedCategory: "necessary",
            },
            {
                title: formatMessage({ id: 'COOKIE_CONSENT_SECTION_ANALYTICS_TITLE' }),
                description: formatMessage({ id: 'COOKIE_CONSENT_SECTION_ANALYTICS_DESCRIPTION' }),
                linkedCategory: "analytics",
            },
            {
                title: formatMessage({ id: 'COOKIE_CONSENT_SECTION_MARKETING_TITLE' }),
                description: formatMessage({ id: 'COOKIE_CONSENT_SECTION_MARKETING_DESCRIPTION' }),
                linkedCategory: "marketing",
            },
        ],
    },
});

const syncGtmConsent = () => updateGtmConsent({
    analytics: CookieConsent.acceptedCategory('analytics'),
    marketing: CookieConsent.acceptedCategory('marketing'),
});

export default function CookieConsentSetup() {
    // same resolution as IntlProviderWrapper: user locale when logged in, otherwise userLanguage
    const locale = useSelector((state: ReduxState) => state?.user?.user?.locale || state?.userLanguage?.userLanguage);
    const shortLocale = locale?.substring(0, 2).toLowerCase();
    const language = SUPPORTED_LANGUAGES.includes(shortLocale) ? shortLocale : 'en';

    useEffect(() => {
        CookieConsent.run({
            autoShow: true,
            mode: "opt-in",
            categories: {
                necessary: {
                    enabled: true,
                    readOnly: true,
                },
                analytics: {},
                marketing: {},
            },

            // first consent (accept all / reject all / save) and on every page load once consent exists
            onConsent: syncGtmConsent,
            // later changes from the preferences modal (accept all / reject all / save)
            onChange: syncGtmConsent,

            language: {
                default: language,
                translations: Object.fromEntries(SUPPORTED_LANGUAGES.map(lang => [lang, buildTranslation])),
            },
        });
    }, []);

    useEffect(() => {
        CookieConsent.setLanguage(language);
    }, [language]);

    return null;
}
